import React, { useEffect, useState, useMemo } from "react";
import { Form } from "react-bootstrap";
import { ReactSelect } from "@services/ui-components";

interface SalaryComponentProps {
    service?: any;
    updateSalaryValue: (newValue: number | undefined) => void;
    updateSalaryType: (newType: string) => void;
    currencySign: string;
    salaryError?: boolean;
    salary?: number;
    currType?: string;
    onInteraction?: () => void;
    disabled?: boolean;
}

const SalaryComponent: React.FC<SalaryComponentProps> = ({
    updateSalaryValue,
    updateSalaryType,
    currencySign,
    salaryError,
    salary,
    currType,
    onInteraction,
    disabled = false,
}) => {
    const [inputValue, setInputValue] = useState<string>(
        typeof salary === "number" && !isNaN(salary) ? salary.toString() : ""
    );
    const [hasInteracted, setHasInteracted] = useState(false);

    useEffect(() => {
        if (typeof salary === "number" && !isNaN(salary)) {
            setInputValue(salary.toString());
        } else {
            setInputValue("");
        }
    }, [salary]);

    const options = useMemo(
        () => [
            { value: "%", label: "%" },
            { value: currencySign, label: currencySign },
        ],
        [currencySign]
    );

    const getSelectedOption = useMemo(() => {
        return options.find((option) => option.value === currType) || null;
    }, [currType, options]);

    const handleSalaryTypeChange = (selectedOption: any) => {
        updateSalaryType(selectedOption.value);
        onInteraction?.();
    };

    const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!hasInteracted) {
            setHasInteracted(true);
        }

        let raw = e.target.value;
        raw = raw.replace(",", ".");
        if (raw.startsWith(".")) return;

        if (currType === "%") {
            raw = raw.replace(/[^0-9]/g, "");
        } else {
            const parts = raw.split(".");
            const intPart = parts[0].replace(/[^0-9]/g, "");
            const decimalPart = parts[1]?.replace(/[^0-9]/g, "") ?? "";

            raw = intPart + (parts.length > 1 ? "." + decimalPart : "");
        }

        const [intPart = "", decimalPart = ""] = raw.split(".");

        if (intPart.length > 6) return;

        if (intPart.length > 1 && intPart.startsWith("0")) return;

        if (currType !== "%" && decimalPart.length > 2) return;

        setInputValue(raw);

        if (raw === "") {
            updateSalaryValue(undefined);
            return;
        }

        const numVal = parseFloat(raw);
        if (isNaN(numVal) || numVal <= 0) {
            updateSalaryValue(undefined);
            return;
        }

        if (currType === "%") {
            if (!Number.isInteger(numVal)) {
                updateSalaryValue(undefined);
                return;
            }
            updateSalaryValue(Math.floor(numVal));
        } else {
            const rounded = Math.round(numVal * 100) / 100;
            updateSalaryValue(rounded);
        }

        onInteraction?.();
    };

    return (
        <div style={{ display: "inline-flex", alignItems: "center" }}>
            <Form.Group>
                <Form.Control
                    name="salary"
                    type="text"
                    onChange={handlePriceChange}
                    placeholder={currType === "%" ? "0" : "0.00"}
                    value={inputValue}
                    style={{
                        maxWidth: "88px",
                        border: salaryError ? "1px solid red" : "",
                    }}
                    isInvalid={salaryError}
                    id={"Salary"}
                    disabled={disabled}
                    onBlur={() => {
                        setHasInteracted(true);
                        onInteraction?.();
                    }}
                />
            </Form.Group>
            <div className="ps-3" style={{ height: "49px" }}>
                <ReactSelect
                    options={options}
                    placeholder={currType || " "}
                    isSearchable={false}
                    onChange={handleSalaryTypeChange}
                    name={"Salary"}
                    value={getSelectedOption}
                    disabled={disabled}
                />
            </div>
        </div>
    );
};

export default SalaryComponent;
